<template>
  <lf-checkbox-dropdown
    class="min-w-full"
    enable-search
    name="naics_codes"
    show-clear-filter
    width-full
    dropdown-width-full
    no-margin
    :options="naicsCodes"
    :placeholder="$t('DEALS.TABLE_HEADERS.NAICS_CODES')"
    :is-loading="loading"
    :filter-icon="filterIcon"
    :toggle-from-parent="toggleFromParent"
    :model-value="formattedModelValue"
    :option-formatter="
      (value: string) => truncateWithEllipsis(value, charLimit)
    "
    @update:model-value="
      (value: unknown[]) => handleModelValueUpdate(value as string[])
    "
    @change-list-visibility="handleListVisibilityChange"
  />
</template>
<script setup lang="ts">
import { computed, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { truncateWithEllipsis } from "@/helpers/formatting";
import { usePromiseWrapper } from "@/hooks/common";

import LfCheckboxDropdown from "@/components/ui/inputs/LfCheckboxDropdown.vue";

const {
  modelValue,
  toggleFromParent = false,
  filterIcon = false,
  loadOnMount = false,
} = defineProps<{
  modelValue: string[];
  toggleFromParent?: boolean;
  filterIcon?: boolean;
  loadOnMount?: boolean;
}>();

const emit = defineEmits<{
  "update:model-value": [value: string[]];
}>();

const { getters, dispatch } = useStore();
const { t } = useI18n();

const charLimit = computed(() => (loadOnMount ? 30 : 20));

const naicsCodesOptions = computed<Record<string, string>>(
  () => getters["options/naicsCodesOptions"]
);

const naicsCodes = computed<Record<string, string>>(() => {
  return Object.entries(naicsCodesOptions.value).reduce(
    (acc: Record<string, string>, [key, value]) => {
      acc[value] = key;
      return acc;
    },
    { "0": t("COMMON.NONE") }
  );
});

const formattedModelValue = computed(() =>
  modelValue.map((item) => (item === "null" ? "0" : item))
);

const { fetchWrapper: getNaicsCodes, loading } = usePromiseWrapper(async () => {
  await dispatch("options/getNaicsCodes");
});

const fetchIfNeeded = async () => {
  if (loading.value || Object.keys(naicsCodesOptions.value).length) {
    return;
  }
  await getNaicsCodes();
};

const handleListVisibilityChange = (isVisible: boolean) => {
  if (isVisible) {
    fetchIfNeeded();
  }
};

const handleModelValueUpdate = (value: string[]) => {
  emit(
    "update:model-value",
    value.map((item) => (item === "0" ? "null" : item))
  );
};

watch(
  () => toggleFromParent,
  (value) => {
    if (value) {
      fetchIfNeeded();
    }
  }
);

onMounted(async () => {
  if (loadOnMount) {
    await fetchIfNeeded();
  }
});
</script>
